import { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { QrCode, Smartphone, Trophy, Play, Wand2, ArrowRight } from "lucide-react";
import { redirectToAuth } from "../../lib/auth";

// Le bandeau 3D est chargé à part : la home doit peindre immédiatement, le poids
// des GLB d'avatars ne doit pas retarder le premier rendu.
const HomeHero3D = lazy(() =>
  import("./HomeHero3D").then((m) => ({ default: m.HomeHero3D }))
);

export interface DemoSet {
  id: string;
  title: string;
  topic: string;
  language: string;
  difficulty?: string;
  questionCount: number;
  highscoreName?: string | null;
  highscoreValue?: number | null;
}

// Badge de langue. Fond saturé et texte blanc : l'ancienne version utilisait des
// pastels sur fond pastel, le badge se voyait à peine.
const LANG_BADGE: Record<string, { code: string; bg: string }> = {
  English: { code: "EN", bg: "bg-sky-300 text-ink" },
  French: { code: "FR", bg: "bg-sakura-400 text-white" },
  German: { code: "DE", bg: "bg-star text-ink" },
  Luxembourgish: { code: "LU", bg: "bg-mint text-ink" },
  Portuguese: { code: "PT", bg: "bg-lavender-300 text-white" },
};

// Liseré coloré en tête de carte. Sert à distinguer les cartes entre elles sans
// recourir à une illustration par quiz.
const CARD_ACCENTS = [
  "from-sakura-400 to-lavender-400",
  "from-sky-300 to-mint",
  "from-star to-sakura-300",
  "from-lavender-400 to-sky-300",
  "from-mint to-star",
  "from-sakura-500 to-star",
];

const STEPS = [
  {
    icon: QrCode,
    title: "Put it on the big screen",
    body: "Open a quiz on any computer connected to a screen or a projector. A QR code appears.",
  },
  {
    icon: Smartphone,
    title: "Everyone joins by scanning",
    body: "No app, no account. Players scan, pick a nickname, and they are in the lobby.",
  },
  {
    icon: Trophy,
    title: "Answer fast, sprint ahead",
    body: "The faster the answer, the further the avatar runs. Everyone lands on the podium at the finish.",
  },
];

function DemoCard({ demo, index }: { demo: DemoSet; index: number }) {
  const navigate = useNavigate();
  const badge =
    LANG_BADGE[demo.language] || {
      code: demo.language.slice(0, 2).toUpperCase(),
      bg: "bg-ink-300 text-white",
    };
  const accent = CARD_ACCENTS[index % CARD_ACCENTS.length];

  return (
    <motion.button
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 5) * 0.06, type: "spring", bounce: 0.35 }}
      whileHover={{ y: -6 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate(`/auto-room?questions=${demo.id}`)}
      className="group basis-[300px] grow-0 shrink-0 text-left bg-white rounded-kawaii overflow-hidden border-2 border-ink/10 shadow-[0_6px_0_0_rgba(46,26,59,0.10)] hover:border-sakura-300 hover:shadow-[0_10px_0_0_rgba(255,61,127,0.18)] transition-[transform,box-shadow,border-color]"
    >
      <div className={`h-2 bg-gradient-to-r ${accent}`} />
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span
            className={`${badge.bg} text-[11px] font-black px-2.5 py-1 rounded-lg uppercase tracking-widest`}
          >
            {badge.code}
          </span>
          <span className="text-[11px] font-black text-ink-300 uppercase tracking-widest">
            {demo.questionCount} questions
          </span>
        </div>

        <h3 className="font-black text-ink text-lg leading-tight mb-1.5 line-clamp-2 min-h-[2.6rem]">
          {demo.title || demo.topic || "Untitled"}
        </h3>
        <p className="text-sm text-ink-300 font-semibold line-clamp-2 min-h-[2.5rem] leading-snug">
          {demo.topic}
        </p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 bg-ink text-white font-black text-sm px-4 py-2 rounded-xl group-hover:bg-sakura-500 transition-colors">
            <Play className="w-3.5 h-3.5 fill-current" />
            Play
          </span>
          {demo.highscoreName && demo.highscoreValue != null && (
            <span className="inline-flex items-center gap-1 text-[11px] font-black text-ink-500 min-w-0">
              <Trophy className="w-3.5 h-3.5 text-star shrink-0" />
              <span className="truncate">{demo.highscoreName}</span>
              <span className="text-ink-300 shrink-0">{demo.highscoreValue.toLocaleString()}</span>
            </span>
          )}
        </div>
      </div>
    </motion.button>
  );
}

export function HomeLanding({ demos }: { demos: DemoSet[] }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen font-kawaii bg-gradient-to-b from-sakura-100 via-[#F7ECFB] to-sky-100 text-ink relative overflow-x-hidden">
      {/* Grille de logos en fond, très discrète (cohérence avec /auto-room) */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage: "url(/assets/bg_grid.svg)",
          backgroundSize: "220px 220px",
          backgroundRepeat: "repeat",
        }}
      />

      <div className="relative z-10">
        {/* ---------- Barre du haut ---------- */}
        <header className="max-w-6xl mx-auto px-5 pt-6 flex items-center justify-between gap-4">
          <img
            src="/assets/logo.svg"
            alt="Think&Sprint"
            className="w-24 sm:w-28 h-auto drop-shadow-sm"
          />
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate("/doc")}
              className="hidden sm:inline-block px-4 py-2.5 rounded-xl font-black text-ink-500 hover:text-ink hover:bg-white/70 transition-colors"
            >
              How to host
            </button>
            <button
              onClick={() => redirectToAuth("/admin")}
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl bg-ink text-white font-black shadow-[0_4px_0_0_rgba(46,26,59,0.35)] hover:bg-ink-700 active:translate-y-0.5 active:shadow-[0_2px_0_0_rgba(46,26,59,0.35)] transition-all"
            >
              <Wand2 className="w-4 h-4" />
              Create a quiz
            </button>
          </div>
        </header>

        {/* ---------- Héros ---------- */}
        <section className="max-w-6xl mx-auto px-5 pt-10 sm:pt-16 text-center">
          <motion.span
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block bg-white border-2 border-ink/10 text-ink-500 text-xs font-black uppercase tracking-[0.18em] px-4 py-2 rounded-full shadow-sm"
          >
            Multiplayer quiz race
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06, type: "spring", bounce: 0.35 }}
            className="mt-6 text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[0.92]"
          >
            Think,{" "}
            <span className="text-sakura-500">Answer,</span>{" "}
            <span className="text-lavender-400">Sprint.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.14 }}
            className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl font-bold text-ink-500 leading-snug"
          >
            Put a quiz on the big screen. Everyone joins by scanning a QR code,
            answers on their phone, and their avatar sprints down the track.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            <a
              href="#demos"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-sakura-500 text-white font-black text-lg shadow-[0_6px_0_0_#B4155A] hover:bg-sakura-400 active:translate-y-1 active:shadow-[0_2px_0_0_#B4155A] transition-all"
            >
              <Play className="w-5 h-5 fill-current" />
              Try a demo
            </a>
            <button
              onClick={() => redirectToAuth("/admin")}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-white text-ink font-black text-lg border-2 border-ink/15 shadow-[0_6px_0_0_rgba(46,26,59,0.12)] hover:border-ink/30 active:translate-y-1 active:shadow-[0_2px_0_0_rgba(46,26,59,0.12)] transition-all"
            >
              Create your own
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </section>

        {/* ---------- Bandeau 3D ---------- */}
        {/* C'est l'argument du produit : on montre la course tout de suite. */}
        <div className="relative h-[210px] sm:h-[260px] lg:h-[290px] mt-2">
          <Suspense fallback={null}>
            <HomeHero3D />
          </Suspense>
          {/* Fondu bas : raccorde le bandeau à la section suivante sans ligne de coupe */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-sky-100 pointer-events-none" />
        </div>

        {/* ---------- Comment ça marche ---------- */}
        <section className="max-w-5xl mx-auto px-5 py-14 sm:py-20">
          <div className="grid gap-5 sm:grid-cols-3">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.08, type: "spring", bounce: 0.35 }}
                className="bg-white/80 backdrop-blur-sm rounded-kawaii border-2 border-ink/10 p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sakura-400 to-lavender-400 flex items-center justify-center shrink-0 shadow-sm">
                    <step.icon className="w-5 h-5 text-white" />
                  </span>
                  <span className="text-4xl font-black text-ink/10 leading-none">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-black text-ink text-lg mb-1.5 leading-tight">
                  {step.title}
                </h3>
                <p className="text-sm font-semibold text-ink-300 leading-snug">
                  {step.body}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ---------- Démos ---------- */}
        {demos.length > 0 && (
          <section id="demos" className="max-w-6xl mx-auto px-5 pb-16 scroll-mt-6">
            <div className="flex items-end justify-between gap-4 mb-7">
              <div>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
                  Try a demo
                </h2>
                <p className="text-ink-300 font-bold mt-1">
                  Ready-made quizzes. Pick one and put it on the screen.
                </p>
              </div>
            </div>
            {/* flex-wrap et pas grid : avec 7 démos, une grille à 3 colonnes laisse
                une carte orpheline collée à gauche. En flex centré, la dernière
                ligne se centre d'elle-même quel que soit le nombre de quiz. */}
            <div className="flex flex-wrap justify-center gap-5">
              {demos.map((d, i) => (
                <DemoCard key={d.id} demo={d} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* ---------- Pied de page ---------- */}
        <footer className="border-t-2 border-ink/10 bg-white/50">
          <div className="max-w-6xl mx-auto px-5 py-7 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm font-bold text-ink-300">
            <button
              onClick={() => navigate("/doc")}
              className="text-ink-500 hover:text-sakura-500 font-black underline underline-offset-4 transition-colors"
            >
              How to host a race
            </button>
            <span className="text-ink/20">·</span>
            <span>
              Made by <span className="text-ink font-black">LMDDC</span>
            </span>
            <span className="text-ink/20">·</span>
            <span>
              3D assets by{" "}
              <a
                href="https://kenney.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink-500 hover:text-sakura-500 underline underline-offset-4"
              >
                Kenney
              </a>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
